import { useCallback, useMemo, useRef, useState } from 'react';
import { Link, matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Building2, Check, ChevronsUpDown, KeyRound, Plus } from 'lucide-react';
import { ORG_TYPE_LABELS } from '@ready/shared';
import { apiUrl, type MyOrg } from '@/lib/api';
import { useCurrentOrg } from '@/lib/current-org';
import { ROLE_LABELS } from '@/lib/labels';
import { useDismiss } from '@/lib/use-dismiss';

/** "Indian Institute of Technology Hyderabad" → "IH". */
export const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter((w) => /^[A-Za-z0-9]/.test(w) && !/^(of|the|and|for|at|in)$/i.test(w))
    .map((w) => w[0]!.toUpperCase())
    .filter((_, i, a) => i === 0 || i === a.length - 1)
    .join('')
    .slice(0, 2) || '·';

export function OrgTile({
  name,
  url,
  className = '',
}: {
  name: string;
  url?: string | null;
  className?: string;
}) {
  if (url)
    return (
      <img
        src={apiUrl(url)}
        alt=""
        className={`size-8 shrink-0 rounded-md border border-border bg-white object-cover ${className}`}
      />
    );
  return (
    <span
      aria-hidden
      className={`flex size-8 shrink-0 items-center justify-center rounded-md bg-primary/15 text-xs font-semibold text-primary-strong ${className}`}
    >
      {initials(name)}
    </span>
  );
}

/** The user's orgs as a tree: each org followed by those under it (when they belong to both). */
function treeOrder(orgs: MyOrg[]) {
  const ids = new Set(orgs.map((o) => o.id));
  const children = new Map<string | null, MyOrg[]>();
  for (const o of orgs) {
    const parent = o.parentId && ids.has(o.parentId) ? o.parentId : null;
    children.set(parent, [...(children.get(parent) ?? []), o]);
  }
  const out: { org: MyOrg; level: number }[] = [];
  const walk = (parent: string | null, level: number) => {
    for (const o of (children.get(parent) ?? []).sort((a, b) => a.name.localeCompare(b.name))) {
      out.push({ org: o, level });
      walk(o.id, level + 1);
    }
  };
  walk(null, 0);
  return out;
}

/**
 * Picks the organization the sidebar's org section works on. Switching on an
 * org page keeps the same section (Library stays Library); elsewhere it opens
 * the org's overview.
 */
export function OrgSwitcher({
  collapsed,
  onNavigate,
}: {
  collapsed: boolean;
  onNavigate?: () => void;
}) {
  const { orgs, current, select } = useCurrentOrg();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const close = useCallback(() => {
    setOpen(false);
    setQ('');
  }, []);
  useDismiss(ref, open, close);
  const rows = useMemo(() => treeOrder(orgs), [orgs]);
  const shown = q
    ? rows.filter((r) => r.org.name.toLowerCase().includes(q.trim().toLowerCase()))
    : rows;

  const choose = (id: string) => {
    select(id);
    close();
    const section = matchPath('/organizations/:orgId/:section/*', pathname)?.params.section;
    navigate(`/organizations/${id}${section ? `/${section}` : ''}`);
    onNavigate?.();
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        title={collapsed ? (current?.name ?? 'Organizations') : undefined}
        onClick={() => setOpen((o) => !o)}
        className={`flex w-full items-center gap-3 rounded-lg text-left hover:bg-muted ${collapsed ? 'justify-center p-1.5' : 'px-2 py-2'}`}
      >
        {current ? (
          <OrgTile name={current.name} url={current.logoUrl} />
        ) : (
          <span className="flex size-8 shrink-0 items-center justify-center rounded-md border border-dashed border-border text-muted-foreground">
            <Building2 className="size-4" aria-hidden />
          </span>
        )}
        {!collapsed && (
          <>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-sm font-medium">
                {current?.name ?? 'Join an organization'}
              </span>
              <span className="block truncate text-xs text-muted-foreground">
                {current
                  ? `${ORG_TYPE_LABELS[current.type as keyof typeof ORG_TYPE_LABELS] ?? 'Organization'}${current.role ? ` · ${ROLE_LABELS[current.role]}` : ' · Parent admin'}`
                  : 'Your university or company'}
              </span>
            </span>
            <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          </>
        )}
      </button>

      {open && (
        <div
          className={`absolute z-40 w-72 rounded-xl border border-border bg-card p-1.5 shadow-lg ${collapsed ? 'left-full top-0 ml-2' : 'left-0 top-full mt-1'}`}
        >
          {orgs.length > 0 && (
            <p className="px-2 pb-1 pt-1.5 text-xs font-medium text-muted-foreground">
              Switch organization
            </p>
          )}
          {orgs.length > 8 && (
            <input
              autoFocus
              type="search"
              aria-label="Find an organization"
              placeholder="Find an organization"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="mb-1 w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm"
            />
          )}
          <ul role="listbox" aria-label="Your organizations" className="max-h-72 overflow-y-auto">
            {shown.map(({ org, level }) => (
              <li key={org.id} role="option" aria-selected={org.id === current?.id}>
                <button
                  type="button"
                  onClick={() => choose(org.id)}
                  className={`flex min-h-11 w-full items-center gap-2.5 rounded-lg py-1.5 pr-2 text-left hover:bg-muted ${['pl-2', 'pl-6', 'pl-10'][Math.min(level, 2)]}`}
                >
                  <OrgTile name={org.name} url={org.logoUrl} className="size-7" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm">{org.name}</span>
                    <span className="block text-xs text-muted-foreground">
                      {ROLE_LABELS[org.role]}
                    </span>
                  </span>
                  {org.id === current?.id && (
                    <Check className="size-4 shrink-0 text-primary" aria-hidden />
                  )}
                </button>
              </li>
            ))}
            {q && shown.length === 0 && (
              <li className="px-2 py-2 text-sm text-muted-foreground">No organization matches.</li>
            )}
          </ul>
          <div className="mt-1 border-t border-border pt-1">
            <Link
              to="/join"
              onClick={() => {
                close();
                onNavigate?.();
              }}
              className="flex min-h-10 items-center gap-2.5 rounded-lg px-2 text-sm hover:bg-muted"
            >
              <KeyRound className="size-4 text-muted-foreground" aria-hidden /> Join with a code
            </Link>
            <Link
              to="/organizations/new"
              onClick={() => {
                close();
                onNavigate?.();
              }}
              className="flex min-h-10 items-center gap-2.5 rounded-lg px-2 text-sm hover:bg-muted"
            >
              <Plus className="size-4 text-muted-foreground" aria-hidden /> Request an organization
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
